import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAllLanguagesQuery } from '../api/endpoints/language';
import { useGetTranslationDataQuery } from '../api/endpoints/translation';
import { addLocale, locale } from 'primereact/api';

const LanguageContext = createContext();

// PrimeReact locale tanımlamaları
const PRIMEREACT_LOCALES = {
    'tr-TR': {
        firstDayOfWeek: 1,
        dayNames: ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'],
        dayNamesShort: ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'],
        dayNamesMin: ['Pz', 'Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct'],
        monthNames: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
        monthNamesShort: ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'],
        today: 'Bugün',
        clear: 'Temizle'
    },
    'en-US': {
        firstDayOfWeek: 0,
        dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
        monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        today: 'Today',
        clear: 'Clear'
    },
    'de-DE': {
        firstDayOfWeek: 1,
        dayNames: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
        dayNamesShort: ['Son', 'Mon', 'Die', 'Mit', 'Don', 'Fre', 'Sam'],
        dayNamesMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
        monthNames: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
        monthNamesShort: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
        today: 'Heute',
        clear: 'Löschen'
    },
    'fr-FR': {
        firstDayOfWeek: 1,
        dayNames: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
        dayNamesShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
        dayNamesMin: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
        monthNames: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
        monthNamesShort: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
        today: "Aujourd'hui",
        clear: 'Effacer'
    },
    'it-IT': {
        firstDayOfWeek: 1,
        dayNames: ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'],
        dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'],
        dayNamesMin: ['Do', 'Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa'],
        monthNames: ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'],
        monthNamesShort: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'],
        today: 'Oggi',
        clear: 'Cancella'
    },
    'ro-RO': {
        firstDayOfWeek: 1,
        dayNames: ['Duminică', 'Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă'],
        dayNamesShort: ['Dum', 'Lun', 'Mar', 'Mie', 'Joi', 'Vin', 'Sâm'],
        dayNamesMin: ['Du', 'Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sâ'],
        monthNames: ['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'],
        monthNamesShort: ['Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun', 'iul', 'Aug', 'Sep', 'Oct', 'Noi', 'Dec'],
        today: 'Astăzi',
        clear: 'Șterge'
    }
};

// PrimeReact locale'lerini kaydet
Object.entries(PRIMEREACT_LOCALES).forEach(([localeName, localeData]) => {
    addLocale(localeName, localeData);
});

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

// Dil mapping - Backend ID'leri ile eşleştirme (locale alanı eklendi)
const COUNTRY_TO_LANGUAGE_MAP = {
    US: { id: 1, code: 'en', name: 'English', locale: 'en-US' },
    TR: { id: 2, code: 'tr', name: 'Türkçe', locale: 'tr-TR' },
    DE: { id: 3, code: 'de', name: 'Deutsch', locale: 'de-DE' },
    FR: { id: 4, code: 'fr', name: 'Français', locale: 'fr-FR' },
    IT: { id: 5, code: 'it', name: 'Italiano', locale: 'it-IT' },
    RO: { id: 6, code: 'ro', name: 'Romanian', locale: 'ro-RO' }
};

export const LanguageProvider = ({ children }) => {
    const { i18n, t } = useTranslation();

    const [selectedCountry, setSelectedCountry] = useState(() => {
        try {
            const saved = localStorage.getItem('selectedLanguage');
            if (saved && (saved.startsWith('{') || saved.startsWith('['))) {
                return JSON.parse(saved);
            }

            const browserLang = navigator.language || navigator.userLanguage;
            const langCode = browserLang.split('-')[0].toUpperCase();

            const supportedLangMap = {
                'EN': 'US',
                'TR': 'TR',
                'DE': 'DE',
                'FR': 'FR',
                'IT': 'IT',
                'RO': 'RO'
            };

            const countryCode = supportedLangMap[langCode] || 'US';
            const languageInfo = COUNTRY_TO_LANGUAGE_MAP[countryCode];

            const defaultCountry = {
                name: languageInfo.name,
                code: countryCode
            };

            localStorage.setItem('selectedLanguage', JSON.stringify(defaultCountry));

            return defaultCountry;
        } catch (error) {
            localStorage.removeItem('selectedLanguage');
            return { name: 'English', code: 'US' };
        }
    });

    // Seçili dilin backend ID'sini al
    const getCurrentLanguageId = () => {
        return COUNTRY_TO_LANGUAGE_MAP[selectedCountry.code]?.id || 2; // Default TR
    };

    // Backend'den dilleri çek (ileride kullanım için)
    const { data: backendLanguages, isLoading } = useGetAllLanguagesQuery();

    // Dynamic translation data - sadece mevcut dil için
    const currentLanguageId = getCurrentLanguageId();
    const { data: translationData, isLoading: isTranslationLoading } = useGetTranslationDataQuery({ LangId: currentLanguageId }, { skip: !currentLanguageId });

    // Seçili dilin kodunu al
    const getCurrentLanguageCode = () => {
        return COUNTRY_TO_LANGUAGE_MAP[selectedCountry.code]?.code || 'tr';
    };

    // Seçili dilin locale'ini al
    const getCurrentLocale = () => {
        return COUNTRY_TO_LANGUAGE_MAP[selectedCountry.code]?.locale || 'tr-TR';
    };

    // Seçili dilin adını al
    const getCurrentLanguageName = () => {
        return COUNTRY_TO_LANGUAGE_MAP[selectedCountry.code]?.name || 'Türkçe';
    };

    // Dil değiştirme fonksiyonu (i18next entegrasyonu ile)
    const changeLanguage = async (newCountry) => {
        setSelectedCountry(newCountry);
        localStorage.setItem('selectedLanguage', JSON.stringify(newCountry));

        // i18next dil değişimi
        const newLanguageCode = COUNTRY_TO_LANGUAGE_MAP[newCountry.code]?.code || 'tr';
        await i18n.changeLanguage(newLanguageCode);

        // Sayfayı yenile (basit çözüm - gelecekte kaldırılabilir)
        window.location.reload();
    };

    // Tüm mevcut dilleri listele
    const getAvailableLanguages = () => {
        return Object.entries(COUNTRY_TO_LANGUAGE_MAP).map(([code, info]) => ({
            countryCode: code,
            ...info
        }));
    };

    // Static çeviri metni getir (i18next t() fonksiyonu)
    const getStaticTranslation = (key, fallback = '', options = {}) => {
        return t(key, { ...options, defaultValue: fallback });
    };

    // Çeviri metni getir (translations array'inden)
    const getTranslationText = (translations, fallback = '') => {
        if (!translations || !Array.isArray(translations)) return fallback;

        const currentLangId = getCurrentLanguageId();
        const translation = translations.find((t) => t.languageId === currentLangId);
        return translation?.text || fallback;
    };

    // Dynamic translation'dan text getir (TranslationId ile)
    const getDynamicTranslation = (translationId, fallback = '') => {
        if (!translationData?.data?.current || !translationId) return fallback;

        // Önce current dildeki çeviriyi ara
        const currentTranslation = translationData.data.current.find((t) => t.id === translationId);
        if (currentTranslation?.text) return currentTranslation.text;

        // Bulunamazsa ve default varsa onu kullan
        if (translationData.data.default) {
            const defaultTranslation = translationData.data.default.find((t) => t.id === translationId);
            if (defaultTranslation?.text) return defaultTranslation.text;
        }

        return fallback;
    };

    // Hem current hem default translation'ları getir
    const getDynamicTranslationWithDefault = (translationId, fallback = '') => {
        if (!translationData?.data?.current || !translationId) return { current: fallback, default: fallback };

        const currentTranslation = translationData.data.current.find((t) => t.id === translationId);
        const defaultTranslation = translationData.data.default?.find((t) => t.id === translationId);

        return {
            current: currentTranslation?.text || fallback,
            default: defaultTranslation?.text || fallback
        };
    };

    // Tüm dynamic translation'ları getir (yeni format)
    const getAllDynamicTranslations = () => {
        return {
            current: translationData?.data?.current || [],
            default: translationData?.data?.default || [],
            totalCount: translationData?.data?.totalCount || 0
        };
    };

    // Kısa kullanım için alias (t() benzeri) - Öncelik sırası: Static > Static(EN) > Dynamic Current > Dynamic Default > Fallback > NOTRANSLATION
    const th = (key, options = {}) => {
        const { translationId, fallback = '', ...i18nOptions } = options;

        // 1. Önce static çeviriden dene (seçili dil)
        if (key) {
            const staticTranslation = t(key, { ...i18nOptions, defaultValue: null });
            if (staticTranslation && staticTranslation !== key) {
                return staticTranslation;
            }
        }

        // 2. Static'te bulunamazsa İngilizceye bak
        if (key && i18n.language !== 'en') {
            const englishTranslation = i18n.getFixedT('en')(key, { ...i18nOptions, defaultValue: null });
            if (englishTranslation && englishTranslation !== key) {
                return englishTranslation;
            }
        }

        // 3. Static'te de bulunamazsa dynamic current'ten dene
        if (translationId && translationData?.data?.current) {
            const currentTranslation = translationData.data.current.find((t) => t.id === translationId);
            if (currentTranslation?.text) {
                return currentTranslation.text;
            }
        }

        // 4. Current'te bulunamazsa dynamic default'tan dene
        if (translationId && translationData?.data?.default) {
            const defaultTranslation = translationData.data.default.find((t) => t.id === translationId);
            if (defaultTranslation?.text) {
                return defaultTranslation.text;
            }
        }

        // 5. Hiçbiri bulunamazsa fallback döndür
        if (fallback) {
            return fallback;
        }

        // 6. Fallback de yoksa NOTRANSLATION döndür
        return 'NOTRANSLATION';
    };

    // i18next dil senkronizasyonu
    useEffect(() => {
        const languageCode = getCurrentLanguageCode();
        if (i18n.language !== languageCode) {
            i18n.changeLanguage(languageCode);
        }
    }, [selectedCountry, i18n, getCurrentLanguageCode]);

    // PrimeReact locale senkronizasyonu
    useEffect(() => {
        const currentLocale = getCurrentLocale();
        locale(currentLocale);
    }, [selectedCountry]);

    // <html lang="..."> senkronizasyonu
    // CSS'teki text-transform: uppercase, İ/I - ı/I dönüşümünü doğru yapabilmek için
    // (SpecialCasing kuralı) dokümanın lang="tr" olmasına ihtiyaç duyar. lang sabit
    // kalırsa (örn. "en") tarayıcı Türkçe harfleri İngilizce kurallarıyla büyütür ve
    // "i" harfi "İ" yerine noktasız "I" olur.
    useEffect(() => {
        document.documentElement.lang = getCurrentLanguageCode();
    }, [selectedCountry]);

    const value = {
        selectedCountry,
        changeLanguage,
        getCurrentLanguageId,
        getCurrentLanguageCode,
        getCurrentLocale,
        getCurrentLanguageName,
        getAvailableLanguages,
        getStaticTranslation,
        getTranslationText,
        getDynamicTranslation,
        getDynamicTranslationWithDefault,
        getAllDynamicTranslations,
        isLoading: isLoading || isTranslationLoading,
        backendLanguages,
        translationData,
        languageMapping: COUNTRY_TO_LANGUAGE_MAP,
        // i18next functions
        t,
        i18n,
        th
    };

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
